import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import {
  fetchMyPaymentsPage,
  isGatewayPaymentProvider,
  matchesBillingHistoryFilter,
  type BillingHistoryFilter,
  type MyPaymentItem
} from '../widget-api/my-payments';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import {
  formatPaymentAmount,
  formatPaymentDateTime,
  paymentProviderLabel,
  paymentStatusLabel
} from '../widget-lib/payment-display';
import { PATH } from '../widget-lib/path-routes';

const statusColor: Record<string, string> = {
  succeeded: 'text-green-600',
  pending: 'text-yellow-600',
  processing: 'text-blue-600',
  cancelled: 'text-gray-500',
  failed: 'text-red-600',
  refunded: 'text-purple-600',
};

function paymentKindLabel(p: MyPaymentItem): string | null {
  if (p.reference_type === 'user_subscription' || (p.reference_type ?? '').includes('subscription')) {
    return 'Абонемент';
  }
  if (p.booking_id != null) {
    return `Запись №${p.booking_id}`;
  }
  if (p.reference_type === 'booking') {
    return 'Запись';
  }
  if (isGatewayPaymentProvider(p.provider)) {
    return 'Онлайн-оплата';
  }
  if (p.provider === 'manual' || p.provider === 'stub') {
    return paymentProviderLabel(p.provider);
  }
  return p.provider ? `Провайдер: ${paymentProviderLabel(p.provider)}` : null;
}

export default function BillingHistoryPage() {
  useWidgetSeo(
    'История платежей',
    'История ваших онлайн-оплат, чеков и транзакций.'
  );
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';
  const { accessToken, bootstrapHint, orgSlug, me } = useEmbedBootstrap();
  const [payments, setPayments] = useState<MyPaymentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<BillingHistoryFilter>('all');

  const filteredPayments = useMemo(
    () => payments.filter((p) => matchesBillingHistoryFilter(p, filter)),
    [payments, filter]
  );

  const fetch = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const page = await fetchMyPaymentsPage(accessToken);
      setPayments(page.items);
      setTotal(page.total);
    } catch {
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  if (bootstrapHint && !orgSlug) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">История платежей</h1>
        <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">История платежей</h1>
        <p className="text-sm text-[var(--text-muted)]">Войдите, чтобы увидеть историю платежей.</p>
        <div className="flex flex-wrap gap-2">
          <Link to={`${PATH.signIn}${suffix}`}
            className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)]">
            Войти
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full min-w-0">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">История платежей</h1>
        <p className="mt-1 text-sm text-[var(--text-muted)]">
          {me ? (me.full_name || 'Пользователь') : ''}
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-[var(--text-muted)]">Загрузка...</p>
      ) : payments.length === 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-[var(--text-muted)]">Платежей пока нет.</p>
          <Link to={`${PATH.schedule}${suffix}`}
            className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)]">
            Записаться на занятие
          </Link>
        </div>
      ) : (
        <>
          <p className="text-xs text-[var(--text-muted)]">Всего записей: {total}</p>
          <div
            className="flex flex-wrap gap-2"
            role="group"
            aria-label="Фильтр по типу платежа"
          >
            {(
              [
                ['all', 'Все'],
                ['online', 'Онлайн'],
                ['subscription', 'Абонемент'],
                ['booking', 'Запись']
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  filter === key
                    ? 'bg-[var(--accent-bg)] text-[var(--accent-fg)]'
                    : 'border border-[var(--border)] bg-[var(--bg-soft)] text-[var(--text)] hover:bg-[var(--bg-elevated)]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {filteredPayments.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">Нет платежей в выбранной категории.</p>
          ) : (
            <ul data-testid="payment-history-list" className="space-y-2">
              {filteredPayments.map((p) => {
                const kind = paymentKindLabel(p);
                return (
                  <li
                    key={p.id}
                    data-testid={`payment-history-item-${p.id}`}
                    className="min-w-0 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-4 text-sm"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <p className="min-w-0 break-words text-base font-semibold tabular-nums text-[var(--text)]">
                        {formatPaymentAmount(p.amount_cents, p.currency)}
                      </p>
                      <p className={`shrink-0 text-xs font-medium ${statusColor[p.status] || 'text-[var(--text-muted)]'}`}>
                        {paymentStatusLabel(p.status)}
                      </p>
                    </div>
                    <p className="mt-1 text-xs text-[var(--text-muted)]">{formatPaymentDateTime(p.created_at)}</p>
                    {kind ? <p className="mt-1 break-words text-xs text-[var(--text)]">{kind}</p> : null}
                    <details className="mt-2 min-w-0 text-xs text-[var(--text-muted)]">
                      <summary className="cursor-pointer select-none">Реквизиты платежа</summary>
                      <dl className="mt-1.5 grid gap-1">
                        <div className="flex flex-wrap gap-x-1">
                          <dt>Провайдер:</dt>
                          <dd className="break-words text-[var(--text)]">{paymentProviderLabel(p.provider)}</dd>
                        </div>
                        <div className="flex flex-wrap gap-x-1">
                          <dt>Номер платежа:</dt>
                          <dd className="tabular-nums text-[var(--text)]">№{p.id}</dd>
                        </div>
                      </dl>
                    </details>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
